import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format, addMonths } from 'date-fns';
import { useTerminology } from '@/hooks/useTerminology';
import { DatePicker } from '@/components/ui/date-picker';

interface CapitalPurchaseWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (eventData: any) => void;
}

export function CapitalPurchaseWizard({ open, onOpenChange, onComplete }: CapitalPurchaseWizardProps) {
  const { getCashMovementTerms, getScenarioTerm } = useTerminology();
  const [step, setStep] = useState(1);
  
  // Step 1: Basic Information
  const [assetType, setAssetType] = useState('');
  const [purchaseCost, setPurchaseCost] = useState('100000');
  const [purchaseDate, setPurchaseDate] = useState<Date>(new Date(2026, 2, 1));

  // Step 2: Financing
  const [financingType, setFinancingType] = useState<'cash' | 'loan' | 'lease'>('cash');
  const [loanInterestRate, setLoanInterestRate] = useState('5');
  const [loanTermMonths, setLoanTermMonths] = useState('60');
  const [leaseMonthlyCost, setLeaseMonthlyCost] = useState('2000');

  // Step 3: Ongoing Costs
  const [includeMaintenance, setIncludeMaintenance] = useState(false);
  const [maintenanceCost, setMaintenanceCost] = useState('500');
  const [maintenanceFrequency, setMaintenanceFrequency] = useState<'monthly' | 'annual'>('monthly');
  const [includeInsurance, setIncludeInsurance] = useState(false);
  const [insuranceCost, setInsuranceCost] = useState('1200');

  // Step 4: Review
  const [description, setDescription] = useState('');

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleComplete = () => {
    const eventData = {
      type: 'capital_purchase',
      assetType,
      purchaseCost: parseFloat(purchaseCost),
      purchaseDate,
      financingType,
      loanInterestRate: financingType === 'loan' ? parseFloat(loanInterestRate) : 0,
      loanTermMonths: financingType === 'loan' ? parseInt(loanTermMonths) : 0,
      leaseMonthlyCost: financingType === 'lease' ? parseFloat(leaseMonthlyCost) : 0,
      includeMaintenance,
      maintenanceCost: includeMaintenance ? parseFloat(maintenanceCost) : 0,
      maintenanceFrequency,
      includeInsurance,
      insuranceCost: includeInsurance ? parseFloat(insuranceCost) : 0,
      description,
    };
    onComplete(eventData);
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setAssetType('');
    setPurchaseCost('100000');
    setPurchaseDate(new Date(2026, 2, 1));
    setFinancingType('cash');
    setLoanInterestRate('5');
    setLoanTermMonths('60');
    setLeaseMonthlyCost('2000');
    setIncludeMaintenance(false);
    setMaintenanceCost('500');
    setMaintenanceFrequency('monthly');
    setIncludeInsurance(false);
    setInsuranceCost('1200');
    setDescription('');
  };

  const calculateLoanPayment = () => {
    if (financingType !== 'loan') return 0;
    const principal = parseFloat(purchaseCost) || 0;
    const monthlyRate = (parseFloat(loanInterestRate) / 100) / 12;
    const numPayments = parseInt(loanTermMonths) || 1;
    
    if (monthlyRate === 0) return principal / numPayments;
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                    (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  const calculateTotalLoanCost = () => {
    if (financingType !== 'loan') return 0;
    const monthlyPayment = calculateLoanPayment();
    const numPayments = parseInt(loanTermMonths) || 1;
    return monthlyPayment * numPayments;
  };

  const calculateMonthlyOngoingCosts = () => {
    let total = 0;
    if (financingType === 'loan') {
      total += calculateLoanPayment();
    } else if (financingType === 'lease') {
      total += parseFloat(leaseMonthlyCost) || 0;
    }
    if (includeMaintenance) {
      const maintenance = parseFloat(maintenanceCost) || 0;
      total += maintenanceFrequency === 'monthly' ? maintenance : maintenance / 12;
    }
    if (includeInsurance) {
      total += (parseFloat(insuranceCost) || 0) / 12;
    }
    return total;
  };

  const calculate12MonthImpact = () => {
    let upfrontCost = 0;
    if (financingType === 'cash') {
      upfrontCost = parseFloat(purchaseCost) || 0;
    }
    
    const monthlyOngoing = calculateMonthlyOngoingCosts();
    const totalOngoing = monthlyOngoing * 12;
    const totalCost = upfrontCost + totalOngoing;
    
    return { upfrontCost, monthlyOngoing, totalOngoing, totalCost };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Capital Purchase Event - Step {step} of 4</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="assetType">Asset Type *</Label>
              <Input
                id="assetType"
                value={assetType}
                onChange={(e) => setAssetType(e.target.value)}
                placeholder="e.g., Manufacturing Equipment, Company Vehicle, Office Furniture"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="purchaseCost">Purchase Cost *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="purchaseCost"
                  type="number"
                  value={purchaseCost}
                  onChange={(e) => setPurchaseCost(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Purchase Date *</Label>
              <DatePicker
                value={purchaseDate}
                onChange={(date) => date && setPurchaseDate(date)}
                dateFormat="PPP"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleNext} disabled={!assetType || !purchaseCost}>
                Next: Financing →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Financing Options</h3>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="cash"
                  checked={financingType === 'cash'}
                  onChange={() => setFinancingType('cash')}
                />
                <Label htmlFor="cash">
                  <span className="font-semibold">Cash Purchase</span>
                  <p className="text-sm text-muted-foreground">Pay full amount upfront</p>
                </Label>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  id="loan"
                  checked={financingType === 'loan'}
                  onChange={() => setFinancingType('loan')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="loan">
                    <span className="font-semibold">Loan</span>
                    <p className="text-sm text-muted-foreground">Finance with monthly repayments</p>
                  </Label>
                  {financingType === 'loan' && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <Label className="w-32">Interest Rate:</Label>
                        <Input
                          type="number"
                          value={loanInterestRate}
                          onChange={(e) => setLoanInterestRate(e.target.value)}
                          className="w-24"
                          step="0.1"
                        />
                        <span className="text-sm">% per year</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="w-32">Loan Term:</Label>
                        <Input
                          type="number"
                          value={loanTermMonths}
                          onChange={(e) => setLoanTermMonths(e.target.value)}
                          className="w-24"
                        />
                        <span className="text-sm">months</span>
                      </div>
                      <div className="bg-muted p-3 rounded">
                        <p className="text-sm">Monthly payment: £{calculateLoanPayment().toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                        <p className="text-sm">Total cost: £{calculateTotalLoanCost().toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                        <p className="text-sm text-muted-foreground">
                          (Interest: £{(calculateTotalLoanCost() - parseFloat(purchaseCost)).toLocaleString(undefined, {maximumFractionDigits: 0})})
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  id="lease"
                  checked={financingType === 'lease'}
                  onChange={() => setFinancingType('lease')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="lease">
                    <span className="font-semibold">Lease</span>
                    <p className="text-sm text-muted-foreground">Monthly lease payments (no ownership)</p>
                  </Label>
                  {financingType === 'lease' && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2">
                        <Label className="w-32">Monthly Cost:</Label>
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={leaseMonthlyCost}
                          onChange={(e) => setLeaseMonthlyCost(e.target.value)}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">per month</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Ongoing Costs →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Ongoing Costs</h3>
            <p className="text-sm text-muted-foreground">Select ongoing costs to include:</p>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="maintenance"
                  checked={includeMaintenance}
                  onCheckedChange={(checked) => setIncludeMaintenance(checked as boolean)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="maintenance" className="font-semibold">Maintenance</Label>
                  {includeMaintenance && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={maintenanceCost}
                          onChange={(e) => setMaintenanceCost(e.target.value)}
                          className="w-32"
                        />
                        <Select value={maintenanceFrequency} onValueChange={(val) => setMaintenanceFrequency(val as 'monthly' | 'annual')}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">per month</SelectItem>
                            <SelectItem value="annual">per year</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {maintenanceFrequency === 'annual' && (
                        <p className="text-sm text-muted-foreground">
                          Monthly equivalent: £{((parseFloat(maintenanceCost) || 0) / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="insurance"
                  checked={includeInsurance}
                  onCheckedChange={(checked) => setIncludeInsurance(checked as boolean)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="insurance" className="font-semibold">Insurance</Label>
                  {includeInsurance && (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={insuranceCost}
                          onChange={(e) => setInsuranceCost(e.target.value)}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">per year</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Monthly equivalent: £{((parseFloat(insuranceCost) || 0) / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm font-semibold">Total Monthly Ongoing Costs:</p>
              <p className="text-lg text-red-600">£{calculateMonthlyOngoingCosts().toLocaleString(undefined, {maximumFractionDigits: 0})}/month</p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Review →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Review Event Summary</h3>

            <div className="space-y-2">
              <p className="font-semibold">Asset: {assetType}</p>
              <p className="text-sm">Purchase Cost: £{parseFloat(purchaseCost).toLocaleString()}</p>
              <p className="text-sm">Purchase Date: {format(purchaseDate, 'MMM d, yyyy')}</p>
              <p className="text-sm">
                Financing: {financingType === 'cash' ? 'Cash' : financingType === 'loan' ? `Loan (${loanInterestRate}% over ${loanTermMonths} months)` : 'Lease'}
              </p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="font-semibold">─── CASH FLOW IMPACT ───────────────────────────</p>

              {financingType === 'cash' && (
                <>
                  <p className="font-semibold text-sm">Upfront Cost ({format(purchaseDate, 'MMM yyyy')}):</p>
                  <p className="text-sm text-red-600">• Purchase: -£{parseFloat(purchaseCost).toLocaleString()}</p>
                </>
              )}

              {calculateMonthlyOngoingCosts() > 0 && (
                <>
                  <p className="font-semibold text-sm mt-4">Monthly Recurring ({format(purchaseDate, 'MMM yyyy')} onwards):</p>
                  {financingType === 'loan' && (
                    <p className="text-sm text-red-600">• Loan payment: -£{calculateLoanPayment().toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                  )}
                  {financingType === 'lease' && (
                    <p className="text-sm text-red-600">• Lease payment: -£{parseFloat(leaseMonthlyCost).toLocaleString()}</p>
                  )}
                  {includeMaintenance && (
                    <p className="text-sm text-red-600">
                      • Maintenance: -£{maintenanceFrequency === 'monthly' ? parseFloat(maintenanceCost).toLocaleString() : ((parseFloat(maintenanceCost) || 0) / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}/month
                    </p>
                  )}
                  {includeInsurance && (
                    <p className="text-sm text-red-600">
                      • Insurance: -£{((parseFloat(insuranceCost) || 0) / 12).toLocaleString(undefined, {maximumFractionDigits: 0})}/month
                    </p>
                  )}
                  <p className="text-sm">Monthly {getCashMovementTerms().out.toLowerCase()}: -£{calculateMonthlyOngoingCosts().toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                </>
              )}

              <p className="font-semibold text-sm mt-4">─── NET IMPACT (12 months) ──────────────────────</p>
              {calculate12MonthImpact().upfrontCost > 0 && (
                <p className="text-sm">Upfront cost: -£{calculate12MonthImpact().upfrontCost.toLocaleString()}</p>
              )}
              <p className="text-sm">12-month ongoing: -£{calculate12MonthImpact().totalOngoing.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-sm font-semibold">
                Total 12-month cost: -£{calculate12MonthImpact().totalCost.toLocaleString(undefined, {maximumFractionDigits: 0})}
              </p>

              {financingType === 'cash' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Cash purchase will require £{parseFloat(purchaseCost).toLocaleString()} upfront. Ensure sufficient cash reserves.
                  </p>
                </div>
              )}

              {financingType === 'loan' && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ Loan will cost £{(calculateTotalLoanCost() - parseFloat(purchaseCost)).toLocaleString(undefined, {maximumFractionDigits: 0})} in interest over {loanTermMonths} months.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-4">
              <Label htmlFor="description">Event Description (optional):</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., New manufacturing equipment to increase capacity"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleComplete}>Add Event to {getScenarioTerm(true)}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
